import { Box, Flex, useToast } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { AssignmentEditTablesContainer } from './AssignmentEditTablesContainer';
import {
  parseAssignmentReceivablesFromJson,
  parseCompleteCurrentMasterLoanTapeSetForLoanProductIds,
} from './utils';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetCreditFacilitiesQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useGetLatestAssignmentReceivablesQuery } from 'packages/loanProduct/requests/requests.gql.generated';
import { AreMasterLoanTapesAssignedCheckContainer } from 'packages/loanProduct/ui/AreMasterLoanTapesAssignedCheckContainer';
import { AssignmentLoadingSkeleton } from 'packages/loanProduct/ui/AssignmentSkeletonLoadingTables';
import { AssignmentIncompleteCurrentMasterLoanTapeSetAlert } from 'packages/loanProduct/ui/AssignmentTemplate';
import { useGetOrganizationLoanProductsQuery } from 'packages/organization/requests/organization.gql.generated';

export const AssignmentEditBody = ({
  masterLoanTapeIds,
  reportingDate,
  fileVersion,
  organizationId,
}: {
  masterLoanTapeIds: string[];
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  organizationId: string;
}) => {
  const toast = useToast();
  const {
    data: latestAssignmentReceivablesData,
    loading: latestAssignmentReceivablesLoading,
  } = useGetLatestAssignmentReceivablesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      masterLoanTapeIds,
    },
  });

  const {
    data: creditFacilitiesForBorrowerData,
    loading: creditFacilitiesForBorrowerLoading,
  } = useGetCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: { organizationId },
  });

  if (
    !creditFacilitiesForBorrowerData ||
    !creditFacilitiesForBorrowerData.creditFacilities ||
    creditFacilitiesForBorrowerLoading ||
    !latestAssignmentReceivablesData ||
    !latestAssignmentReceivablesData.latestAssignmentReceivablesWithMetadata ||
    latestAssignmentReceivablesLoading
  ) {
    return (
      <AssignmentLoadingSkeleton
        dataTestId="loading-assignment-edit-tables-container"
        editMode
      />
    );
  }

  const {
    latestAssignmentReceivablesWithMetadata,
  } = latestAssignmentReceivablesData;
  const parsedLatestAssignmentReceivables = parseAssignmentReceivablesFromJson(
    latestAssignmentReceivablesWithMetadata.assignmentReceivables,
  );

  const creditFacilities = _.map(
    creditFacilitiesForBorrowerData.creditFacilities,
    ({ id, borrowerEntityName }) => {
      return { id, borrowerEntityName };
    },
  );

  return (
    <Flex justify="flex-start" width="auto" flexGrow={1} height="100%">
      <AssignmentEditTablesContainer
        assignmentReceivables={parsedLatestAssignmentReceivables}
        masterLoanTapeIds={masterLoanTapeIds}
        reportingDate={reportingDate}
        fileVersion={fileVersion}
        creditFacilities={creditFacilities}
        organizationId={organizationId}
      />
    </Flex>
  );
};

export const AssignmentEditContainer = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const toast = useToast();
  const {
    data: organizationLoanProductsData,
    loading: organizationLoanProductsLoading,
  } = useGetOrganizationLoanProductsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      organizationId,
    },
  });

  if (
    !organizationLoanProductsData ||
    !organizationLoanProductsData.organization ||
    organizationLoanProductsLoading
  ) {
    return (
      <AssignmentLoadingSkeleton
        dataTestId="loading-assignment-edit-container"
        editMode
      />
    );
  }

  const loanProductsForOrganization =
    organizationLoanProductsData.organization.loanProducts;

  const completeCurrentMasterLoanTapeSetForLoanProductIds = parseCompleteCurrentMasterLoanTapeSetForLoanProductIds(
    loanProductsForOrganization,
  );

  if (!completeCurrentMasterLoanTapeSetForLoanProductIds) {
    return <AssignmentIncompleteCurrentMasterLoanTapeSetAlert />;
  }

  const {
    fileVersion,
    reportingDate,
    masterLoanTapeIds,
  } = completeCurrentMasterLoanTapeSetForLoanProductIds;

  return (
    <Box>
      <AreMasterLoanTapesAssignedCheckContainer
        masterLoanTapeIds={masterLoanTapeIds}
        fileVersion={fileVersion}
        reportingDate={reportingDate}
      >
        <AssignmentEditBody
          masterLoanTapeIds={masterLoanTapeIds}
          reportingDate={reportingDate}
          fileVersion={fileVersion}
          organizationId={organizationId}
        />
      </AreMasterLoanTapesAssignedCheckContainer>
    </Box>
  );
};
